import React, { useState, useEffect, useRef, useMemo } from 'react';

// ─── Icons ────────────────────────────────────────────────────────────────────
const CheckIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ChevronRightIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const MusicIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
  </svg>
);
const FileAudioIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="13 2 13 9 20 9" />
  </svg>
);
const LockIcon = ({ className = 'w-3 h-3' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const ClockIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const WalletIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
  </svg>
);
const RefreshIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);
const PaletteIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

// ─── Confetti ─────────────────────────────────────────────────────────────────
const CONFETTI_COLORS = ['#3B5BDB', '#7048E8', '#F59F00', '#37B24D', '#F03E3E', '#1C7ED6', '#E64980', '#0CA678'];

const Confetti = () => {
  const pieces = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    id: i,
    left: 5 + (i / 36) * 90 + (Math.random() - 0.5) * 8,
    delay: Math.random() * 0.6,
    duration: 1.2 + Math.random() * 0.8,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 5 + Math.random() * 6,
    round: i % 3 === 0,
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 10 }}>
      {pieces.map(p => (
        <div
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.round ? p.size : p.size * 0.5,
            background: p.color,
            borderRadius: p.round ? '50%' : 2,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
};


// ─── Tab Step Bar ─────────────────────────────────────────────────────────────
const STEPS = ['Niche', 'Configure', 'Subtitles', 'Generate', 'Done'];

const TabBar = ({ current, onNavigate }) => (
  <div className="flex items-center gap-1 sm:gap-4 px-3 sm:px-5 py-3 border-b border-gray-100">
    {STEPS.map((label, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div
          key={i}
          onClick={() => done && onNavigate(i)}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap transition-all duration-200 text-[11px] sm:text-xs ${
            active
              ? 'text-primary font-semibold cursor-default'
              : done
              ? 'text-gray-500 font-medium hover:text-gray-700 cursor-pointer'
              : 'text-gray-500 font-medium cursor-default'
          }`}
          style={active ? { background: '#EEF2FF' } : {}}
        >
          {done && <CheckIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
          {label}
        </div>
      );
    })}
  </div>
);

// ─── Continue Button (with shimmer) ──────────────────────────────────────────
const ContinueBtn = ({ onClick, disabled = false, label = 'Continue', loading = false }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
      disabled
        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
        : 'bg-primary text-white hover:bg-primary-dark active:scale-95 btn-shimmer'
    }`}
  >
    {loading ? (
      <>
        <span className="spinner" />
        <span>Generating…</span>
      </>
    ) : (
      <>
        <span>{label}</span>
        {!disabled && <ChevronRightIcon className="w-3.5 h-3.5" />}
      </>
    )}
  </button>
);

// ─── Step 1: Niche ────────────────────────────────────────────────────────────
const NICHES = [
  {
    name: 'Game of Thrones (What If)',
    img: '/niche-images/1.jpeg',
    desc: 'Can be used for Game of Thrones Facts, Fall Asleep To, or Lore etc.',
    selected: true,
  },
  {
    name: 'Naruto Lore',
    img: '/niche-images/2.jpeg',
    desc: 'Can be used for Naruto Facts, Fall Asleep To, or Lore etc.',
  },
  {
    name: 'Space Facts',
    img: '/niche-images/3.jpeg',
    desc: 'Can be used for Space Facts, Fall Asleep To, or Lore etc.',
  },
  {
    name: 'Pokemon Facts',
    img: '/niche-images/4.jpeg',
    desc: 'Can be used for Pokemon Facts, Fall Asleep To, or Lore etc.',
  },
];

const NicheCard = ({ niche, selected }) => {
  const { name, img, desc } = niche;

  return (
    <div
      className={`rounded-xl overflow-hidden border transition-all duration-200 ${
        selected
          ? 'niche-selected-glow cursor-default'
          : 'border-gray-200 hover:border-gray-300 cursor-pointer hover:shadow-sm'
      }`}
      style={{ borderColor: selected ? '#3B5BDB' : undefined, borderWidth: selected ? 1 : undefined }}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden rounded-t-xl" style={{ height: 160 }}>
        <img
          src={img}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          loading="lazy"
        />
        {/* Dark gradient overlay at bottom for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-8"
             style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)' }} />
        {selected && (
          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-md">
            <CheckIcon className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5 bg-white">
        <p className="text-xs font-bold text-gray-900 leading-tight">{name}</p>
        <p className="text-[10px] text-gray-400 mt-0.5 leading-snug line-clamp-2">{desc}</p>
        <span className="mt-1.5 inline-block text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
          ultra-long-form video
        </span>
      </div>
    </div>
  );
};

const Step1Niche = ({ onNext }) => (
  <div className="px-3 py-4 sm:px-5 sm:py-5">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="text-base font-bold text-gray-900">Choose Your Content Style</h2>
        <p className="text-xs text-gray-400 mt-0.5">Select the visual style and theme for your video</p>
      </div>
      <span className="relative overflow-hidden flex items-center gap-1 bg-primary text-white text-[11px] font-semibold px-2.5 py-1 rounded-full animate-bounce ml-3 flex-shrink-0 btn-shimmer">
        👆 Start Demo
      </span>
    </div>

    <div className="grid grid-cols-2 gap-2.5 mb-5">
      {NICHES.map((niche) => (
        <NicheCard
          key={niche.name}
          niche={niche}
          selected={!!niche.selected}
        />
      ))}
    </div>

    <div className="flex justify-end">
      <ContinueBtn onClick={onNext} />
    </div>
  </div>
);

// ─── Step 2: Configure ────────────────────────────────────────────────────────
const MUSIC_TRACKS = ['Timeless', 'Destiny', 'Dark Ambience', 'Moonshine', 'Lovely', 'Apocolypse'];

const UploadIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ChevronDownIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FrameIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <rect x="7" y="7" width="10" height="10" rx="1" />
  </svg>
);
const CloudUploadIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const BORDER_PRESETS = [
  { id: 'black',      label: 'Black',      bg: '#111111', border: '1px solid #333' },
  { id: 'lightgrey',  label: 'Light Grey', bg: '#c8c8c8', border: '1px solid #aaa' },
  { id: 'white',      label: 'White',      bg: '#ffffff', border: '1px solid #d1d5db' },
];
const PARTICLE_EFFECTS = ['Snow', 'Rain', 'Dust', 'Embers'];
const FLOW_DIRECTIONS = ['Up', 'Down', 'Left', 'Right'];

const ToggleSwitch = ({ on, onToggle, size = 'md' }) => {
  const w = size === 'sm' ? 32 : 38;
  const h = size === 'sm' ? 18 : 22;
  const knob = size === 'sm' ? 12 : 16;
  const offset = size === 'sm' ? 3 : 3;
  return (
    <button
      onClick={onToggle}
      style={{ width: w, height: h, borderRadius: h / 2, background: on ? '#3B5BDB' : '#d1d5db', position: 'relative', border: 'none', cursor: 'pointer', padding: 0, transition: 'background 0.2s', flexShrink: 0 }}
    >
      <span style={{ position: 'absolute', top: (h - knob) / 2, left: on ? w - knob - offset : offset, width: knob, height: knob, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.18s cubic-bezier(0.4,0,0.2,1)', display: 'block' }} />
    </button>
  );
};

const SliderRow = ({ label, min, max, defaultValue, unit = '' }) => {
  const [val, setVal] = useState(defaultValue);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-gray-500 w-14 flex-shrink-0">{label}</span>
      <input type="range" min={min} max={max} value={val} onChange={e => setVal(+e.target.value)} className="flex-1 h-1 accent-primary" style={{ cursor: 'pointer' }} />
      <span className="text-[10px] font-semibold text-gray-600 w-8 text-right flex-shrink-0">{val}{unit}</span>
    </div>
  );
};

const Step2Configure = ({ onNext }) => {
  const [title, setTitle] = useState('');
  const [music, setMusic] = useState('Timeless');
  const [musicOpen, setMusicOpen] = useState(false);
  const [borderStyle, setBorderStyle] = useState(null);
  const [particleOn, setParticleOn] = useState(false);
  const [particleEffect, setParticleEffect] = useState('Embers');
  const [flowDir, setFlowDir] = useState('Up');
  const [flowOpen, setFlowOpen] = useState(false);
  const fileInputRef = useRef(null);
  const bgFileRef = useRef(null);

  return (
    <div className="px-3 py-4 sm:px-5 sm:py-5">
      <h2 className="text-base font-bold text-gray-900 mb-0.5">Configure Your Video</h2>
      <p className="text-xs text-gray-400 mb-4">Set up your video details and select audio files</p>

      {/* Selected niche preview banner */}
      <div className="relative rounded-xl overflow-hidden mb-5" style={{ height: 72 }}>
        <img
          src="/niche-images/1.jpeg"
          alt="Game of Thrones (What If)"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 100%)' }} />
        <div className="relative z-10 flex items-center gap-3 h-full px-3">
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-bold leading-tight">Game of Thrones (What If)</p>
            <p className="text-white/55 text-[10px] mt-0.5">Fantasy lore · Ultra-long-form</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <LockIcon className="w-3 h-3 text-white/55" />
            <span className="text-[10px] font-semibold text-white/80 bg-white/10 border border-white/20 px-2 py-0.5 rounded-full">
              Selected
            </span>
          </div>
        </div>
      </div>

      {/* Video Title */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Video Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter your video title..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-300 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
        />
      </div>

      {/* TTS Audio */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
          TTS Audio <span className="text-red-400">*</span>
        </label>
        <div className="flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <FileAudioIcon className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-800">demo-voiceover.mp3</p>
            <p className="text-[10px] text-gray-400 mt-0.5">3.02 MB · Pre-loaded</p>
          </div>
          <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex-shrink-0">Ready</span>
          <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 px-2 py-1 rounded-lg transition-all flex-shrink-0"
          >
            <UploadIcon className="w-3 h-3" />
            Upload
          </button>
        </div>
      </div>

      {/* Background Music */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Music</label>
        <div className="relative" style={{ zIndex: musicOpen ? 100 : 'auto' }}>
          <button
            onClick={() => setMusicOpen(o => !o)}
            className="w-full flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50 hover:bg-gray-100/80 transition-all text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
              <MusicIcon className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800">{music}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Background track</p>
            </div>
            <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${musicOpen ? 'rotate-180' : ''}`} />
          </button>
          {musicOpen && (
            <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }}>
              {MUSIC_TRACKS.map((track) => (
                <button key={track} onClick={() => { setMusic(track); setMusicOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${music === track ? 'bg-primary/5' : ''}`}
                >
                  <MusicIcon className={`w-3.5 h-3.5 flex-shrink-0 ${music === track ? 'text-primary' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium flex-1 ${music === track ? 'text-primary font-semibold' : 'text-gray-700'}`}>{track}</span>
                  {music === track && <CheckIcon className="w-3 h-3 text-primary flex-shrink-0" />}
                </button>
              ))}
              <div className="border-t border-gray-100">
                <button onClick={() => { setMusicOpen(false); fileInputRef.current?.click(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                >
                  <UploadIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-500">Upload your own</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Border & Background ───────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl overflow-hidden mb-5">
        {/* Section header */}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
          <FrameIcon className="w-3.5 h-3.5 text-gray-500" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-gray-800">Border &amp; Background</p>
            <p className="text-[10px] text-gray-400 leading-tight">Add a decorative frame and background behind your video content <span className="text-gray-400">(Optional)</span></p>
          </div>
        </div>

        <div className="p-3 space-y-4">
          {/* Top row: border presets + bg upload side by side */}
          <div className="flex gap-3">
            {/* Border style presets */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-gray-600 mb-2">Border Style</p>
              <div className="flex gap-2">
                {BORDER_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => setBorderStyle(b => b === preset.id ? null : preset.id)}
                    className="flex flex-col items-center gap-1 transition-all"
                    style={{ width: 52 }}
                  >
                    <div
                      className="w-full rounded-md transition-all"
                      style={{
                        height: 36,
                        background: preset.bg,
                        border: borderStyle === preset.id ? '2px solid #3B5BDB' : `2px solid ${preset.id === 'white' ? '#d1d5db' : 'transparent'}`,
                        boxShadow: borderStyle === preset.id ? '0 0 0 1px rgba(59,91,219,0.3)' : 'none',
                      }}
                    />
                    <span className="text-[9px] font-medium text-gray-500 leading-tight">{preset.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Background image upload */}
            <div className="w-24 flex-shrink-0">
              <p className="text-[10px] font-semibold text-gray-600 mb-2">Background</p>
              <input ref={bgFileRef} type="file" accept="image/*" className="hidden" />
              <button
                onClick={() => bgFileRef.current?.click()}
                className="w-full h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary/50 hover:bg-primary/[0.03] flex flex-col items-center justify-center gap-1 transition-all"
              >
                <CloudUploadIcon className="w-4 h-4 text-gray-400" />
                <span className="text-[8px] text-gray-400 font-medium leading-tight text-center px-1">Upload Image</span>
              </button>
            </div>
          </div>

          {/* Live Preview */}
          <div>
            <p className="text-[10px] font-semibold text-gray-600 mb-1.5">Preview</p>
            <div
              className="w-full rounded-lg overflow-hidden relative flex items-center justify-center"
              style={{
                aspectRatio: '16/9',
                background: borderStyle === 'black' ? '#111' : borderStyle === 'lightgrey' ? '#c8c8c8' : borderStyle === 'white' ? '#fff' : '#1a1a2e',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
              }}
            >
              {/* Particle overlay hint */}
              {particleOn && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
                  {Array.from({ length: 18 }).map((_, i) => (
                    <div key={i} style={{
                      position: 'absolute',
                      left: `${5 + (i / 18) * 90}%`,
                      top: `${10 + Math.sin(i * 1.7) * 35}%`,
                      width: particleEffect === 'Embers' ? 3 : particleEffect === 'Snow' ? 4 : 2,
                      height: particleEffect === 'Rain' ? 8 : 3,
                      borderRadius: particleEffect === 'Rain' ? 1 : '50%',
                      background: particleEffect === 'Embers' ? '#ff6b35' : particleEffect === 'Snow' ? '#e8f4ff' : '#ffffff',
                      opacity: 0.4 + (i % 3) * 0.15,
                    }} />
                  ))}
                </div>
              )}
              {/* Inner video area */}
              <div
                className="relative flex items-center justify-center"
                style={{
                  width: borderStyle ? '80%' : '100%',
                  height: borderStyle ? '78%' : '100%',
                  background: '#0d0d1a',
                  borderRadius: borderStyle ? 4 : 0,
                  border: borderStyle === 'black' ? '2px solid #000' : borderStyle === 'lightgrey' ? '2px solid #9ca3af' : borderStyle === 'white' ? '2px solid #e5e7eb' : 'none',
                  transition: 'all 0.25s ease',
                  zIndex: 2,
                }}
              >
                <p className="text-[10px] text-white/30 font-medium select-none">Your video content</p>
              </div>
            </div>
          </div>

          {/* Particle Overlay */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50">
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-none">✨</span>
                <span className="text-xs font-semibold text-gray-800">Particle Overlay</span>
              </div>
              <ToggleSwitch on={particleOn} onToggle={() => setParticleOn(o => !o)} />
            </div>

            {particleOn && (
              <div className="px-3 py-3 space-y-3">
                {/* Effect pills */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 mb-1.5">Effect</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {PARTICLE_EFFECTS.map(e => (
                      <button
                        key={e}
                        onClick={() => setParticleEffect(e)}
                        className="px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all border"
                        style={{
                          background: particleEffect === e ? '#3B5BDB' : '#f3f4f6',
                          color: particleEffect === e ? '#fff' : '#6b7280',
                          borderColor: particleEffect === e ? '#3B5BDB' : 'transparent',
                        }}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Flow direction */}
                <div className="relative" style={{ zIndex: flowOpen ? 50 : 'auto' }}>
                  <p className="text-[10px] font-semibold text-gray-500 mb-1.5">Flow Direction</p>
                  <button
                    onClick={() => setFlowOpen(o => !o)}
                    className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-left hover:bg-gray-50 transition-all"
                  >
                    <span className="text-xs text-gray-700 font-medium">{flowDir}</span>
                    <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform ${flowOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {flowOpen && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg overflow-hidden" style={{ zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                      {FLOW_DIRECTIONS.map(d => (
                        <button key={d} onClick={() => { setFlowDir(d); setFlowOpen(false); }}
                          className={`w-full px-2.5 py-1.5 text-left text-xs font-medium hover:bg-gray-50 transition-colors flex items-center justify-between ${flowDir === d ? 'text-primary bg-primary/5' : 'text-gray-700'}`}
                        >
                          {d}
                          {flowDir === d && <CheckIcon className="w-3 h-3 text-primary" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sliders */}
                <div className="space-y-2">
                  <SliderRow label="Amount"  min={1}  max={100} defaultValue={40} />
                  <SliderRow label="Speed"   min={1}  max={10}  defaultValue={4}  />
                  <SliderRow label="Size"    min={1}  max={20}  defaultValue={6}  />
                  <SliderRow label="Opacity" min={10} max={100} defaultValue={60} unit="%" />
                  <SliderRow label="Density" min={1}  max={10}  defaultValue={5}  />
                </div>

                {/* Particle colour */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 mb-1.5">Particle Colour</p>
                  <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white w-fit">
                    <div className="w-4 h-4 rounded flex-shrink-0" style={{ background: particleEffect === 'Embers' ? '#ff6b35' : particleEffect === 'Snow' ? '#d0eaff' : '#ffffff', border: '1px solid rgba(0,0,0,0.1)' }} />
                    <span className="text-[10px] font-mono text-gray-600">
                      {particleEffect === 'Embers' ? '#FF6B35' : particleEffect === 'Snow' ? '#D0EAFF' : '#FFFFFF'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <ContinueBtn onClick={onNext} />
      </div>
    </div>
  );
};

// ─── Step 3: Subtitles ────────────────────────────────────────────────────────
const SUBTITLE_FONTS = [
  { name: 'Impact',      family: 'Impact, "Arial Narrow", sans-serif' },
  { name: 'Arial Black', family: '"Arial Black", Gadget, sans-serif' }, // pre-selected
  { name: 'Oswald',      family: "'Oswald', sans-serif" },
  { name: 'Bebas Neue',  family: "'Bebas Neue', sans-serif" },
  { name: 'Montserrat',  family: "'Montserrat', sans-serif" },
  { name: 'Georgia',     family: 'Georgia, serif' },
];

const DisabledField = ({ label, children }) => (
  <div className="mb-4 opacity-40 pointer-events-none select-none">
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const Step3Subtitles = ({ onNext }) => {
  const [wbwOn, setWbwOn] = useState(true);

  return (
  <div className="px-3 py-4 sm:px-5 sm:py-5">
    <h2 className="text-base font-bold text-gray-900 mb-0.5">Subtitle Styling</h2>
    <p className="text-xs text-gray-400 mb-4">Customize the appearance and position of your video subtitles</p>

    {/* Word-by-Word Highlighting */}
    <div
      className="rounded-xl border mb-4 p-3"
      style={{ borderColor: wbwOn ? '#3B5BDB' : '#e5e7eb', background: wbwOn ? '#f5f7ff' : '#fafafa' }}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-900">Word-by-Word Highlighting</span>
          <span className="text-[9px] font-bold text-white bg-primary px-1.5 py-0.5 rounded-full leading-none">NEW</span>
        </div>
        <button
          onClick={() => setWbwOn(o => !o)}
          className="flex-shrink-0"
          style={{
            width: 38,
            height: 22,
            borderRadius: 11,
            background: wbwOn ? '#3B5BDB' : '#d1d5db',
            position: 'relative',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            transition: 'background 0.2s',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 3,
              left: wbwOn ? 19 : 3,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
              transition: 'left 0.18s cubic-bezier(0.4,0,0.2,1)',
              display: 'block',
            }}
          />
        </button>
      </div>
      <p className="text-[10px] text-gray-500 mb-3 leading-relaxed">
        YouTube-style karaoke effect where each word pops as it's spoken
      </p>
      <div className={`grid grid-cols-2 gap-2 transition-opacity duration-200 ${wbwOn ? 'opacity-100' : 'opacity-35 pointer-events-none'}`}>
        <div>
          <p className="text-[10px] font-semibold text-gray-500 mb-1">Highlight Color</p>
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
            <div className="w-4 h-4 rounded flex-shrink-0" style={{ background: '#68C0FF', border: '1px solid rgba(0,0,0,0.08)' }} />
            <span className="text-[10px] font-mono text-gray-600">#68C0FF</span>
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-gray-500 mb-1">Highlight Scale</p>
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
            <span className="text-xs font-bold text-primary">115%</span>
          </div>
        </div>
      </div>
    </div>

    {/* Font grid */}
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-600 mb-2">Font</label>
      <div className="grid grid-cols-2 gap-1.5 pointer-events-none select-none">
        {SUBTITLE_FONTS.map((font, i) => (
          <div
            key={font.name}
            className="rounded-xl p-2.5 text-center flex flex-col items-center justify-center transition-all"
            style={{
              background: '#12122a',
              border: `1.5px solid ${i === 1 ? '#3B5BDB' : 'rgba(255,255,255,0.07)'}`,
              boxShadow: i === 1 ? '0 0 0 1px rgba(59,91,219,0.25), 0 0 8px rgba(59,91,219,0.2)' : 'none',
              minHeight: 64,
            }}
          >
            <p
              style={{
                fontFamily: font.family,
                fontWeight: 700,
                fontSize: 18,
                color: '#ffffff',
                lineHeight: 1.2,
                marginBottom: 5,
                letterSpacing: '0.01em',
              }}
            >
              THE QUICK
            </p>
            <p style={{ fontSize: 8, color: '#5a5a7a', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
              {font.name}
            </p>
          </div>
        ))}
      </div>
    </div>

    <DisabledField label="Font Size">
      <div className="flex items-center gap-3">
        <input type="range" min={24} max={100} defaultValue={48} className="flex-1" disabled />
        <span className="text-xs font-bold text-gray-500 w-8 text-right">48px</span>
      </div>
    </DisabledField>

    <div className="grid grid-cols-2 gap-3 mb-4 opacity-40 pointer-events-none select-none">
      {[['Text Color', '#FFFFFF', 'white'], ['Outline Color', '#000000', 'black']].map(([lbl, hex, bg]) => (
        <div key={lbl}>
          <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
            <PaletteIcon /> {lbl}
          </label>
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-2 bg-gray-50">
            <div className="w-4 h-4 rounded border border-gray-300 flex-shrink-0" style={{ background: bg }} />
            <span className="text-[10px] font-mono text-gray-500">{hex}</span>
          </div>
        </div>
      ))}
    </div>

    <DisabledField label="Positioning">
      <div className="grid grid-cols-3 gap-2">
        {['Left', 'Center', 'Right'].map((p, i) => (
          <div key={p} className={`border rounded-lg py-2 text-center text-xs font-semibold ${i === 1 ? 'border-gray-400 bg-gray-100 text-gray-700' : 'border-gray-200 text-gray-400'}`}>
            {p}
          </div>
        ))}
      </div>
    </DisabledField>

    <div className="flex justify-end mt-1">
      <ContinueBtn onClick={onNext} />
    </div>
  </div>
  );
};

// ─── Step 4: Generate ─────────────────────────────────────────────────────────
const Step4Generate = ({ onNext }) => {
  const [checked, setChecked] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleStart = () => {
    if (!checked) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); onNext(); }, 2000);
  };

  return (
    <div className="px-3 py-4 sm:px-5 sm:py-5">
      <h2 className="text-base font-bold text-gray-900 mb-0.5">Confirm &amp; Create</h2>
      <p className="text-xs text-gray-400 mb-4">Review the cost and confirm to start creating your video</p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="border border-gray-200 rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <ClockIcon className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Estimated Cost</span>
          </div>
          <p className="text-2xl font-bold text-primary leading-none">2h 12m</p>
          <p className="text-[10px] text-gray-400 mt-1">Video export hours</p>
        </div>
        <div className="border border-gray-200 rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <WalletIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Your Balance</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 leading-none">260h</p>
          <p className="text-[10px] text-gray-400 mt-1">After: <span className="font-semibold text-gray-600">257h 48m</span></p>
        </div>
      </div>

      <label
        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all mb-4 ${
          checked ? 'border-primary/40 bg-primary/[0.04]' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div
          onClick={() => setChecked(c => !c)}
          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
            checked ? 'bg-primary border-primary' : 'border-gray-300'
          }`}
        >
          {checked && <CheckIcon className="w-2.5 h-2.5 text-white" />}
        </div>
        <span className="text-xs text-gray-600 leading-relaxed">
          I confirm and authorize the use of <strong className="text-gray-900">2h 12m</strong> to create this video
        </span>
      </label>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded-full bg-green-500 flex items-center justify-center">
            <CheckIcon className="w-2 h-2 text-white" />
          </div>
          <span className="text-xs text-green-700 font-medium">Ready to create · ~54 min</span>
        </div>
        <ContinueBtn onClick={handleStart} disabled={!checked} label="Start Generating" loading={loading} />
      </div>
    </div>
  );
};

// ─── Step 5: Done ─────────────────────────────────────────────────────────────
const Step5Done = ({ onReset }) => {
  const [phase, setPhase] = useState('hourglass');
  const [showConfetti, setShowConfetti] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setPhase('video'), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase === 'video') {
      videoRef.current?.play().catch(() => {});
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 2500);
      return () => clearTimeout(t);
    }
  }, [phase]);

  return (
    <div className="relative px-3 py-4 sm:px-5 sm:py-5">
      {showConfetti && <Confetti />}

      {phase === 'hourglass' ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="hourglass-anim text-5xl mb-3">⏳</div>
          <h2 className="text-sm font-bold text-gray-800 mb-1">Video Job Queued</h2>
          <p className="text-xs text-gray-400 text-center">Your video is being generated…</p>
        </div>
      ) : (
        <div className="step-fade">
          <div className="mb-3">
            <h2 className="text-base font-bold text-gray-900">Your Video is Ready! 🎉</h2>
            <p className="text-xs text-gray-400 mt-0.5">Game of Thrones (What If) · demo-voiceover.mp3</p>
          </div>

          <div className="rounded-xl overflow-hidden bg-black mb-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}>
            <video
              ref={videoRef}
              src="https://www.w3schools.com/html/mov_bbb.mp4"
              controls
              autoPlay
              playsInline
              muted
              className="w-full"
              style={{ display: 'block', maxHeight: 300 }}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <ClockIcon className="w-3.5 h-3.5 text-primary" /> 2h 12m used
              </span>
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                <CheckIcon className="w-3 h-3" /> Generated
              </span>
            </div>
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-lg font-semibold text-xs hover:bg-primary-dark transition-all active:scale-95 btn-shimmer"
            >
              <RefreshIcon className="w-3.5 h-3.5" />
              Generate Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState('forward');

  const next = () => {
    setDirection('forward');
    setStep(s => Math.min(s + 1, 4));
  };
  const reset = () => {
    setDirection('forward');
    setStep(0);
  };
  const navigate = (i) => {
    setDirection(i < step ? 'back' : 'forward');
    setStep(i);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-0 sm:p-4" style={{ background: 'transparent' }}>
      <div
        className="w-full bg-white rounded-none sm:rounded-2xl"
        style={{ maxWidth: 680, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)' }}
      >
        <TabBar current={step} onNavigate={navigate} />

        {/* Slide wrapper — overflow-hidden keeps slide animation contained but allows dropdowns via z-index stacking */}
        <div key={step} className={direction === 'forward' ? 'step-slide-fwd' : 'step-slide-back'} style={{ overflow: 'visible' }}>
          {step === 0 && <Step1Niche onNext={next} />}
          {step === 1 && <Step2Configure onNext={next} />}
          {step === 2 && <Step3Subtitles onNext={next} />}
          {step === 3 && <Step4Generate onNext={next} />}
          {step === 4 && <Step5Done onReset={reset} />}
        </div>
      </div>
    </div>
  );
}
